/**
 * Assistant tool registry.
 *
 * Built-in tools mirror the GUI 1:1 (they call the same CadApi the panels
 * do). Three meta-tools make the assistant self-extending:
 *   run_js      — one-off JS against the `api` object (rapid prototyping)
 *   define_tool — persist a named tool (JS body + JSON-schema params); it
 *                 joins the assistant's toolset immediately
 *   publish_tool — surface a defined tool in the GUI toolbar, where users
 *                 run it through an auto-generated parameter form
 *
 * Custom tools run locally in the browser with full app access — same trust
 * level as the app itself; they are stored per-browser in IndexedDB.
 */

import type { CadApi } from './cadApi';
import { getSetting, setSetting } from '@core/storage/settings';

export interface ToolSchema {
  type: 'object';
  properties: Record<string, unknown>;
  required?: string[];
}

export interface ToolDef {
  name: string;
  description: string;
  parameters: ToolSchema;
  execute(args: Record<string, unknown>, api: CadApi): Promise<unknown>;
}

export interface CustomTool {
  name: string;
  description: string;
  parameters: ToolSchema;
  /** JS body; receives (api, params); may use await; return value = result. */
  code: string;
  published: boolean;
}

const obj = (properties: Record<string, unknown>, required?: string[]): ToolSchema => ({
  type: 'object',
  properties,
  ...(required ? { required } : {}),
});
const num = (description: string): object => ({ type: 'number', description });
const str = (description: string, enums?: string[]): object => ({
  type: 'string',
  description,
  ...(enums ? { enum: enums } : {}),
});

const ENTITY_SCHEMA = {
  type: 'array',
  description:
    'Sketch entities (2D, mm, on the sketch plane): ' +
    '{kind:"rect",min:[x,y],max:[x,y]} | {kind:"circle",center:[x,y],radius} | ' +
    '{kind:"ellipse",center:[x,y],rx,ry} (axis-aligned) | ' +
    '{kind:"line",a:[x,y],b:[x,y]} | {kind:"arc",center:[x,y],radius,startAngle,endAngle (radians, CCW)} | ' +
    '{kind:"spline",points:[[x,y],...],closed?:true} (fair B-spline THROUGH the points — hull curves) | ' +
    '{kind:"text",at:[x,y],height:mm,text:"..."} (glyph outlines — extrude to emboss, cut to engrave) | ' +
    '{kind:"polygon",center:[x,y],radius,sides?:6} (regular N-gon macro — never hand-compute vertices) | ' +
    '{kind:"slot",a:[x,y],b:[x,y],width} (obround macro: centerline a→b, overall width) | ' +
    '{kind:"rounded_rect",min:[x,y],max:[x,y],cornerRadius} | ' +
    '{kind:"star",center:[x,y],outerRadius,innerRadius,points?:5} | ' +
    '{kind:"gear",center:[x,y],module,teeth,pressureAngleDeg?:20,boreDiameter?,rotateDeg?} (exact involute spur gear; pitch⌀=module·teeth; MESHING: space centers (z1+z2)·module/2 apart AND phase the second gear rotateDeg:180/teeth2 so teeth interleave — verify with interference). ' +
    'Closed loops become profiles; nested loops are holes; open line/arc chains are sweep paths.',
  items: { type: 'object' },
};

/**
 * Map common failure messages to a machine code + concrete NEXT STEP the
 * agent can act on — turning trial-and-error round-trips into one retry.
 */
export function classifyToolError(message: string): { code: string; suggestion: string } | null {
  // Patterns are anchored to the EXACT wording thrown by the kernels/api —
  // when an error message changes, update its rule (tests pin each pair).
  const RULES: [RegExp, string, string][] = [
    [/no closed (profile|loop)|requires a sketch profile/i, 'OPEN_PROFILE',
      'call sketch_info(<sketchId>) — openChains[].start/end show exactly where the loop leaks; close the gap or add the missing entity'],
    [/no open curve|with an open curve/i, 'NEEDS_OPEN_PATH',
      'sweep paths and loft spines must be OPEN chains — pass a sketch whose line/arc/spline does NOT close into a loop; sketch_info shows which chains are open'],
    [/exact B-rep|B-rep kernel|mesh-only|display-only/i, 'NO_BREP',
      'this body has no exact shape (imported mesh or mesh-kernel session) — rebuild it from native features or re-import the STEP file'],
    [/missing from the asset store/i, 'MISSING_ASSET',
      're-import the original STEP/IGES file — its stored source is gone from this browser profile'],
    [/(fillet|chamfer) of .*failed|try a smaller value/i, 'DRESSUP_TOO_BIG',
      'the radius/distance exceeds what adjoining faces allow — call edge_info, pick fewer edges, or use a smaller value'],
    [/no earlier bodies exist|Nothing to (cut|join|intersect)/i, 'CONSUMED_OR_EMPTY',
      'no visible body exists for the boolean to act on — call scene_info and target the NEWEST visible feature in the chain (earlier bodies may have been consumed)'],
    [/checkpoint.*(different project|older than)/i, 'STALE_CHECKPOINT',
      'the checkpoint token is no longer valid — take a fresh checkpoint() before the next risky step'],
    [/offset may consume the body|Press\/pull failed/i, 'PRESSPULL_TOO_DEEP',
      'the negative offset cuts deeper than the body — measure the wall with measure_feature/cross_section and use a smaller offset'],
    [/Draft of .*failed|No faces to draft/i, 'DRAFT_FAILED',
      'try a smaller angle, or pass explicit face centroids from face_info (curved faces cannot be drafted)'],
    [/Thread needs|Pitch too coarse|Thread fuse failed|length > pitch/i, 'THREAD_PARAMS',
      'check diameter vs pitch (ISO coarse: M3=0.5 M5=0.8 M6=1.0 M8=1.25 M10=1.5) and length > pitch'],
  ];
  for (const [re, code, suggestion] of RULES) {
    if (re.test(message)) return { code, suggestion };
  }
  return null;
}

/** Tools dry_run may trial: timeline-mutating, no external side effects. */
const DRY_RUNNABLE = new Set([
  'create_sketch', 'extrude', 'revolve', 'sweep', 'loft', 'primitive', 'coil',
  'thread', 'draft', 'press_pull', 'hole', 'pattern', 'fillet', 'chamfer',
  'shell', 'mirror_body', 'move_body', 'scale_body', 'group_bodies',
  'instance_body', 'mate', 'insert_part', 'update_feature', 'delete_feature',
  'add_sketch_entities', 'pattern_sketch_entities', 'set_material',
]);

export function builtinTools(): ToolDef[] {
  return [
    {
      name: 'list_features',
      description:
        'List the parametric timeline: every feature with id, name, type, status and params. Call this first to understand the current design.',
      parameters: obj({}),
      execute: async (_a, api) => api.listFeatures(),
    },
    {
      name: 'body_info',
      description: 'Bounding box (mm), volume (mm³), triangle count of a body feature.',
      parameters: obj({ id: str('feature id') }, ['id']),
      execute: async (a, api) => api.bodyInfo(a.id as string) ?? { error: 'no body' },
    },
    {
      name: 'create_sketch',
      description:
        'Create a 2D sketch. Plane: {kind:"datum",plane:"XY"|"YZ"|"XZ",offset} | {kind:"on_face",bodyId,faceIndex} or {kind:"on_face",bodyId,near:[x,y,z]} (sketch on any PLANAR face of a body — resolved from face_info, rides the face through edits; EASIEST way to build on top of an existing body) | {kind:"free",origin:[x,y,z],normal:[x,y,z]}. Z-up, millimetres.',
      parameters: obj(
        { entities: ENTITY_SCHEMA, plane: { type: 'object' }, name: str('optional name') },
        ['entities'],
      ),
      execute: async (a, api) => api.createSketch(a as never),
    },
    {
      name: 'add_sketch_entities',
      description:
        'APPEND entities (same schema as create_sketch, macros included) to an existing sketch — e.g. add a bore circle to a gear, or extra holes to a plate. Downstream features regenerate.',
      parameters: obj(
        { sketchId: str('sketch feature id'), entities: ENTITY_SCHEMA },
        ['sketchId', 'entities'],
      ),
      execute: async (a, api) => api.addSketchEntities(a as never),
    },
    {
      name: 'pattern_sketch_entities',
      description:
        'CLONE sketch entities IN the sketch: circular (count around center, e.g. a bolt circle of 6 holes from 1 circle) or linear (count × step). count INCLUDES the original. indices = which entities to clone (from sketch_info order; omit = all). Prefer this over computing rotated coordinates yourself.',
      parameters: obj(
        {
          sketchId: str('sketch feature id'),
          kind: str('pattern kind', ['circular', 'linear']),
          count: num('total instances (2..100), original included'),
          indices: { type: 'array', items: { type: 'number' }, description: 'entity indices to clone; omit = all' },
          center: { type: 'array', items: { type: 'number' }, description: 'circular: [x,y] rotation center' },
          totalAngleDeg: num('circular: span, default 360'),
          step: { type: 'array', items: { type: 'number' }, description: 'linear: [dx,dy] between instances' },
        },
        ['sketchId', 'kind', 'count'],
      ),
      execute: async (a, api) => api.patternSketchEntities(a as never),
    },
    {
      name: 'sketch_info',
      description:
        'DIAGNOSE a sketch before extruding: closed profiles (index/area mm²/bbox/hole count — profile:0 is the largest) and OPEN chains with their endpoints + gap. If extrude fails or a profile is missing, call this: an open chain\'s start/end tell you exactly where the loop leaks. Free and instant — prefer it over guessing.',
      parameters: obj({ sketchId: str('sketch feature id') }, ['sketchId']),
      execute: async (a, api) => api.sketchInfo(a.sketchId as string),
    },
    {
      name: 'extrude',
      description:
        'Extrude a sketch profile into a solid. distance in mm (negative = opposite the plane normal). op: new-body|join|cut|intersect (cut/join/intersect target ALL earlier visible bodies). endScale tapers the far cap (0 = cone/pyramid apex).',
      parameters: obj(
        {
          sketchId: str('sketch feature id'),
          distance: num('mm'),
          op: str('boolean op', ['new-body', 'join', 'cut', 'intersect']),
          endScale: num('far-cap scale, default 1'),
          profileIndex: num('which closed profile (0 = largest); OMIT to extrude ALL profiles (text, multi-region sketches)'),
        },
        ['sketchId', 'distance'],
      ),
      execute: async (a, api) => api.extrude(a as never),
    },
    {
      name: 'revolve',
      description:
        'Revolve a sketch profile around an axis IN the sketch plane (axisOrigin/axisDirection are 2D sketch coords). Profile must stay on one side of the axis.',
      parameters: obj(
        {
          sketchId: str('sketch feature id'),
          angleDeg: num('default 360'),
          axisOrigin: { type: 'array', items: { type: 'number' } },
          axisDirection: { type: 'array', items: { type: 'number' } },
          op: str('boolean op', ['new-body', 'join', 'cut', 'intersect']),
        },
        ['sketchId'],
      ),
      execute: async (a, api) => api.revolve(a as never),
    },
    {
      name: 'sweep',
      description:
        'Sweep a profile sketch along an OPEN path (line/arc chain) from another sketch. Draw the profile centered on its sketch origin.',
      parameters: obj(
        { profileSketchId: str('closed-profile sketch'), pathSketchId: str('open-path sketch') },
        ['profileSketchId', 'pathSketchId'],
      ),
      execute: async (a, api) => api.sweep(a as never),
    },
    {
      name: 'loft',
      description:
        'Loft a solid through 2+ closed sketch profiles IN ORDER (boat hull stations, transitions). ruled=true gives straight sections; default smooth. spineSketchId (open curve through the sections, e.g. a keel line) guides the surface between stations.',
      parameters: obj(
        {
          sketchIds: {
            type: 'array',
            items: { type: 'string' },
            description: '2+ sketch feature ids, in loft order',
          },
          ruled: { type: 'boolean' },
          spineSketchId: str('optional guide-curve sketch (open spline/line chain)'),
          op: str('boolean op', ['new-body', 'join', 'cut', 'intersect']),
        },
        ['sketchIds'],
      ),
      execute: async (a, api) => api.loft(a as never),
    },
    {
      name: 'move_body',
      description:
        'Move/rotate a body (consumes it). copies>0 keeps the original and arrays N copies at multiples of the transform (linear for moves, circular for rotations).',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          translation: { type: 'array', items: { type: 'number' }, description: '[x,y,z] mm' },
          rotateAxis: str('world axis', ['X', 'Y', 'Z']),
          angleDeg: num('rotation about the body center'),
          copies: num('array count, default 0'),
        },
        ['bodyId'],
      ),
      execute: async (a, api) => api.moveBody(a as never),
    },
    {
      name: 'scale_body',
      description: 'Scale a body per-axis (or uniformly via factor) about its center.',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          factor: num('uniform factor'),
          factors: { type: 'array', items: { type: 'number' }, description: '[fx,fy,fz]' },
        },
        ['bodyId'],
      ),
      execute: async (a, api) => api.scaleBody(a as never),
    },
    {
      name: 'mirror_body',
      description: 'Mirror a body across a world plane. keepOriginal=true gives a symmetric pair.',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          plane: str('mirror plane', ['YZ', 'XZ', 'XY']),
          offset: num('plane offset, default 0'),
          keepOriginal: { type: 'boolean' },
        },
        ['bodyId', 'plane'],
      ),
      execute: async (a, api) => api.mirrorBody(a as never),
    },
    {
      name: 'group_bodies',
      description: 'Group 2+ bodies into one unit (consumes them; delete the group to ungroup).',
      parameters: obj(
        { bodyIds: { type: 'array', items: { type: 'string' } } },
        ['bodyIds'],
      ),
      execute: async (a, api) => api.groupBodies(a as never),
    },
    {
      name: 'instance_body',
      description:
        'Place a component instance: a transformed copy that TRACKS its source (source stays visible; editing it updates all instances).',
      parameters: obj(
        {
          sourceId: str('source body feature id'),
          translation: { type: 'array', items: { type: 'number' } },
          rotateAxis: str('world axis', ['X', 'Y', 'Z']),
          angleDeg: num('rotation'),
          copies: num('extra arrayed placements'),
        },
        ['sourceId'],
      ),
      execute: async (a, api) => api.instanceBody(a as never),
    },
    {
      name: 'list_parameters',
      description: 'List the document\'s named parameters (name, expression, evaluated value).',
      parameters: obj({}),
      execute: async (_a, api) => api.listParameters(),
    },
    {
      name: 'set_parameter',
      description:
        'Create or update a named document parameter, e.g. set_parameter("width","60") then use it anywhere a number goes by passing {"$expr":"width/2"} instead of the number (extrude distance, sketch constraint values…). Changing a parameter regenerates every feature using it. Expressions may reference other parameters; functions sin/cos/tan (degrees), sqrt, abs, min, max, round; constant pi.',
      parameters: obj(
        { name: str('identifier, e.g. width'), expr: str('expression, e.g. "60" or "width/2"') },
        ['name', 'expr'],
      ),
      execute: async (a, api) => api.setParameter(a.name as string, a.expr as string),
    },
    {
      name: 'delete_parameter',
      description: 'Delete a named parameter (features still referencing it will error).',
      parameters: obj({ name: str('parameter name') }, ['name']),
      execute: async (a, api) => api.deleteParameter(a.name as string),
    },
    {
      name: 'list_projects',
      description: 'List workspace projects (assembly part sources): id, name, feature count, current flag.',
      parameters: obj({}),
      execute: async (_a, api) => api.listProjects(),
    },
    {
      name: 'create_project',
      description:
        'Create a NEW empty project and switch to it. Use for building a reusable part in its own file (then insert_part it into an assembly) or starting a fresh design. The previous project is saved automatically.',
      parameters: obj({ name: str('project name') }, []),
      execute: async (a, api) => api.createProject(a.name as string | undefined),
    },
    {
      name: 'switch_project',
      description:
        'Switch the live document to an existing project by id (from list_projects). Edits after this apply to that project.',
      parameters: obj({ id: str('project id') }, ['id']),
      execute: async (a, api) => api.switchProject(a.id as string),
    },
    {
      name: 'delete_project',
      description:
        'PERMANENTLY delete a project by id (from list_projects) — cannot be undone. If it is the current project, the app switches to another (or creates a blank one if it was the last). Any assembly that insert_part-ed this project will error until relinked. Destructive: only when the user clearly wants the project gone.',
      parameters: obj({ id: str('project id from list_projects') }, ['id']),
      execute: async (a, api) => api.deleteProject(a.id as string),
    },
    {
      name: 'insert_part',
      description:
        'ASSEMBLY: insert another project as a linked component (one body). The link is parametric — editing the source project updates the assembly. Position/rotate on insert or move it later (move_body/update_feature on {position,rotationEuler}).',
      parameters: obj(
        {
          projectId: str('source project id (see list_projects)'),
          position: { type: 'array', items: { type: 'number' }, description: '[x,y,z] mm' },
          rotationEulerDeg: { type: 'array', items: { type: 'number' }, description: 'Euler XYZ, degrees' },
          name: str('optional feature name'),
        },
        ['projectId'],
      ),
      execute: async (a, api) => api.insertPart(a as never),
    },
    {
      name: 'hole',
      description:
        'HOLE WIZARD: drill holes into a body at world-space face points (normal = face normal at that point, default [0,0,1] for top faces). style: plain|counterbore|countersink. depth: mm or "through". Clearance diameters: M3=3.4 M4=4.5 M5=5.5 M6=6.6 M8=9. Much better than sketch-circle+cut.',
      parameters: obj(
        {
          bodyId: str('target body feature id'),
          holes: {
            type: 'array',
            description: '[{point:[x,y,z], normal?:[x,y,z]}] positions ON a face',
            items: { type: 'object' },
          },
          diameter: num('bore diameter mm, default 5.5 (M5 clearance)'),
          depth: { description: 'mm or "through" (default)' },
          style: str('hole style', ['plain', 'counterbore', 'countersink']),
          cbDiameter: num('counterbore diameter'),
          cbDepth: num('counterbore depth'),
          csDiameter: num('countersink diameter'),
          csAngle: num('countersink included angle, default 90'),
        },
        ['bodyId', 'holes'],
      ),
      execute: async (a, api) => api.hole(a as never),
    },
    {
      name: 'pattern',
      description:
        'PATTERN a body: linear (count × step vector) or circular (count around axis at center, totalAngleDeg default 360). count may be {"$expr":"paramName"} for parametric arrays. Consumes the body; original = instance #0.',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          kind: str('pattern kind', ['linear', 'circular']),
          count: { description: 'instances (≥2), number or {"$expr":...}' },
          step: { type: 'array', items: { type: 'number' }, description: 'linear: [x,y,z] mm between instances' },
          axis: str('circular: rotation axis', ['X', 'Y', 'Z']),
          center: { type: 'array', items: { type: 'number' }, description: 'circular: axis point' },
          totalAngleDeg: num('circular span, default 360'),
        },
        ['bodyId', 'kind', 'count'],
      ),
      execute: async (a, api) => api.pattern(a as never),
    },
    {
      name: 'face_info',
      description:
        'LIST a body\'s exact B-rep faces: planar {centroid, outward normal} and cylindrical {axis, axisPoint, radius}. Use it to aim hole points (a planar face centroid + its normal), pick sketch-on-face targets, and find the cylindrical faces concentric mates need — no screenshot required. World mm.',
      parameters: obj({ bodyId: str('body feature id') }, ['bodyId']),
      execute: async (a, api) => api.faceInfo(a.bodyId as string),
    },
    {
      name: 'edge_info',
      description:
        'LIST a body\'s B-rep edge midpoints — these are the handles fillet/chamfer take. Pick the midpoints of the edges you want (e.g. the four verticals of a box top) and pass them as `edges`.',
      parameters: obj({ bodyId: str('body feature id') }, ['bodyId']),
      execute: async (a, api) => api.edgeInfo(a.bodyId as string),
    },
    {
      name: 'fillet',
      description:
        'FILLET (round) a body\'s edges with the given radius. Omit `edges` to round ALL edges; to round specific ones, pass their midpoints from edge_info. Radius must be smaller than adjoining faces allow or the kernel errors.',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          radius: num('fillet radius mm'),
          edges: { type: 'array', items: { type: 'array', items: { type: 'number' } }, description: 'optional [x,y,z] edge midpoints from edge_info' },
        },
        ['bodyId', 'radius'],
      ),
      execute: async (a, api) => api.fillet(a as never),
    },
    {
      name: 'chamfer',
      description:
        'CHAMFER (bevel) a body\'s edges by the given setback distance. Same edge selection as fillet: omit `edges` for all, or pass midpoints from edge_info.',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          distance: num('chamfer setback mm'),
          edges: { type: 'array', items: { type: 'array', items: { type: 'number' } }, description: 'optional [x,y,z] edge midpoints from edge_info' },
        },
        ['bodyId', 'distance'],
      ),
      execute: async (a, api) => api.chamfer(a as never),
    },
    {
      name: 'shell',
      description:
        'SHELL: hollow a body into walls of `thickness`, opening the top (default), bottom, or none (closed hollow). Boxes/enclosures: extrude the outer shape, then shell.',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          thickness: num('wall thickness mm'),
          open: str('which side to open', ['top', 'bottom', 'none']),
        },
        ['bodyId', 'thickness'],
      ),
      execute: async (a, api) => api.shell(a as never),
    },
    {
      name: 'primitive',
      description:
        'ANALYTIC solid in ONE call — no sketch: box (dx/dy/dz, `at` = center), cylinder (radius/height, base at `at`, along axis), sphere (radius, center `at`), cone (radius→radius2 over height; radius2:0 = apex), torus (majorRadius/minorRadius, center `at`, axis ⊥ ring plane). Numerics take {"$expr":...}. op cut/join acts on all earlier bodies — a torus cut makes an O-ring groove, a cylinder join makes a boss. Prefer this over sketch+extrude for basic shapes; sphere/torus are ONLY available here.',
      parameters: obj(
        {
          shape: str('primitive kind', ['box', 'cylinder', 'sphere', 'cone', 'torus']),
          at: { type: 'array', items: { type: 'number' }, description: '[x,y,z] center/base point, default origin' },
          axis: str('long axis, default Z', ['X', 'Y', 'Z']),
          dx: { description: 'box: X size' }, dy: { description: 'box: Y size' }, dz: { description: 'box: Z size' },
          radius: { description: 'cylinder/sphere/cone base radius' },
          radius2: { description: 'cone top radius (0 = pointed)' },
          height: { description: 'cylinder/cone height' },
          majorRadius: { description: 'torus ring radius (center to tube center)' },
          minorRadius: { description: 'torus tube radius' },
          op: str('boolean op, default new-body', ['new-body', 'join', 'cut', 'intersect']),
          name: str('optional feature name'),
        },
        ['shape'],
      ),
      execute: async (a, api) => api.primitive(a as never),
    },
    {
      name: 'press_pull',
      description:
        'DIRECT-MODEL any planar face: prism the face nearest facePoint (from face_info centroids) along its outward normal by offset — positive ADDS material, negative REMOVES. Works on any B-rep body including imported STEP parts. The SketchUp push/pull, as a parametric feature.',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          facePoint: { type: 'array', items: { type: 'number' }, description: '[x,y,z] on/near the planar face (face_info centroid)' },
          offset: { description: 'mm along the outward normal; + adds, − removes' },
          name: str('optional feature name'),
        },
        ['bodyId', 'facePoint', 'offset'],
      ),
      execute: async (a, api) => api.pressPull(a as never),
    },
    {
      name: 'draft',
      description:
        'MOLD DRAFT: tilt walls by angleDeg about a neutral (parting) plane ⊥ the pull axis at neutralOffset. Omit faces = all planar walls parallel to the pull axis; or pass face centroids from face_info. Essential for molded/cast parts (typical 1–3°).',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          angleDeg: { description: 'draft angle in degrees (sign flips direction)' },
          axis: str('pull direction, default Z', ['X', 'Y', 'Z']),
          neutralOffset: { description: 'parting-plane position along the axis, default 0' },
          faces: { type: 'array', items: { type: 'array', items: { type: 'number' } }, description: 'optional [x,y,z] face centroids to draft' },
        },
        ['bodyId', 'angleDeg'],
      ),
      execute: async (a, api) => api.draft(a as never),
    },
    {
      name: 'thread',
      description:
        'REAL ISO metric thread (60° truncated-V profile, not the coil approximation). op join/new-body = EXTERNAL stud (ridges fused on a minor-⌀ core) — e.g. an M10 bolt shank; op cut = INTERNAL tap: first cut a hole of the minor ⌀ (≈ diameter − 0.65·pitch... use hole/cylinder), then thread with op:"cut" at the same axis. Pitch defaults from the ISO coarse table (M2–M20); pass pitch for fine threads.',
      parameters: obj(
        {
          diameter: num('nominal/major ⌀ mm (10 = M10)'),
          length: { description: 'threaded length mm (> pitch)' },
          pitch: num('mm per turn; omit = ISO coarse for the diameter'),
          axis: str('thread axis, default Z', ['X', 'Y', 'Z']),
          axisOrigin: { type: 'array', items: { type: 'number' }, description: 'axis start point, default origin' },
          leftHanded: { type: 'boolean', description: 'reverse-threaded' },
          op: str('boolean op, default new-body', ['new-body', 'join', 'cut', 'intersect']),
          name: str('optional feature name'),
        },
        ['diameter', 'length'],
      ),
      execute: async (a, api) => api.thread(a as never),
    },
    {
      name: 'coil',
      description:
        'COIL: exact helical sweep of a circular wire — springs, threads (op:"join" onto a shaft or "cut" for internal threads). radius = helix radius to wire CENTER; pitch = mm advance per turn (must exceed wireDiameter or coils self-intersect); turns may be fractional. Numeric params accept {"$expr":...}.',
      parameters: obj(
        {
          radius: { description: 'helix radius mm, number or {"$expr":...}' },
          pitch: { description: 'mm per turn, number or {"$expr":...}' },
          turns: { description: 'number of turns (fractional ok)' },
          wireDiameter: { description: 'wire thickness mm' },
          axis: str('helix axis, default Z', ['X', 'Y', 'Z']),
          axisOrigin: { type: 'array', items: { type: 'number' }, description: 'axis start point, default [0,0,0]' },
          leftHanded: { type: 'boolean', description: 'reverse the winding' },
          op: str('boolean op, default new-body', ['new-body', 'join', 'cut', 'intersect']),
          name: str('optional feature name'),
        },
        ['radius', 'pitch', 'turns', 'wireDiameter'],
      ),
      execute: async (a, api) => api.coil(a as never),
    },
    {
      name: 'run_fea',
      description:
        'STRUCTURAL FEA (voxel hex, linear elasticity): hold fixture face(s), apply loads, get max von Mises (MPa), max deflection (mm), FACTOR OF SAFETY (yield/maxStress), the hotspot location — and the body is painted with the stress ramp in the viewport. The body needs a material (set_material) or pass material. fixtures/loads name faces by facePoint (use face_info centroids). Each load: force:[x,y,z] N, or magnitude (N, pressed INTO the face — planar faces only; give cylinders an explicit vector). FoS<1 = yields under load; FoS 2+ = healthy. resolution 8-96 voxels along the longest axis (default 36; higher = slower+finer). Design guidance, not certification: voxel boundaries and sharp-corner singularities make peak stresses approximate.',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          fixtures: {
            type: 'array',
            items: { type: 'object' },
            description: '[{facePoint:[x,y,z]}] — faces held rigidly (≥1)',
          },
          loads: {
            type: 'array',
            items: { type: 'object' },
            description: '[{facePoint:[x,y,z], force?:[x,y,z] (N), magnitude?:N (+presses into the face, − pulls), pressure?:MPa (× exact face area → total force; − = suction)}] (≥1); magnitude/pressure need a planar face',
          },
          resolution: num('voxels along longest axis, 8-96 (default 36)'),
          material: str('optional material id override (else the body\'s assigned material)'),
          deformationScale: num('deformed-view exaggeration; omit = auto (~8% of body size), 1 = true scale'),
        },
        ['bodyId', 'fixtures', 'loads'],
      ),
      execute: async (a, api) => api.runFea(a as never),
    },
    {
      name: 'clear_fea',
      description: 'Remove the FEA stress coloring from the viewport (restores normal materials).',
      parameters: obj({}, []),
      execute: async (_a, api) => api.clearFea(),
    },
    {
      name: 'mass_props',
      description:
        'EXACT mass properties of a body from the B-rep (not the display mesh): volume mm³, surface area mm², center of mass. Multiply volume/1000 by density g/cm³ for mass (PLA 1.24, ABS 1.05, alu 2.7, steel 7.85, brass 8.5).',
      parameters: obj({ bodyId: str('body feature id') }, ['bodyId']),
      execute: async (a, api) => api.massProps(a.bodyId as string),
    },
    {
      name: 'list_materials',
      description:
        'The MATERIAL library: id, name, category, render color, and FEA-grade mechanical properties (density g/cm³, Young\'s modulus GPa, Poisson ratio, yield MPa, ultimate MPa, thermal expansion µm/m·K). Assign with set_material; bodies with a material report mass everywhere (scene_info, mass_props).',
      parameters: obj({}, []),
      execute: async (_a, api) => api.listMaterials(),
    },
    {
      name: 'set_material',
      description:
        'ASSIGN a material to a body: sets its render appearance (color/metal/roughness/transparency) AND its physical identity (mass, future FEA). Purely visual+metadata — never regenerates geometry. PATCH semantics: an OMITTED field keeps its current value, an explicit null clears it — so {bodyId, color} tints without dropping the material. Dress-ups/booleans/instances INHERIT the material of their source, so assign once per part. Limitation: materials do not yet cross into insert_part components — assign on the part feature in the assembly as a workaround.',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          material: {
            type: ['string', 'null'],
            description: 'material id from list_materials; null clears; omit to keep current',
          },
          color: {
            type: ['string', 'null'],
            description: '#rrggbb tint over the material; null clears; omit to keep current',
          },
        },
        ['bodyId'],
      ),
      execute: async (a, api) => api.setMaterial(a as never),
    },
    {
      name: 'material_info',
      description:
        'A body\'s EFFECTIVE material (own or inherited through the feature chain) with full mechanical properties + computed mass in grams. Returns null if no material is assigned. This is the property set structural analysis (FEA) consumes.',
      parameters: obj({ bodyId: str('body feature id') }, ['bodyId']),
      execute: async (a, api) => api.materialInfo(a.bodyId as string) ?? { material: null, note: 'no material assigned — set_material first' },
    },
    {
      name: 'scene_info',
      description:
        'ONE-CALL situation report: all visible bodies (id, bbox, exact volume, hasBrep), the parameter table, and any erroring features. Start here instead of list_features + body_info × N when you need the spatial layout.',
      parameters: obj({}, []),
      execute: async (_a, api) => api.sceneInfo(),
    },
    {
      name: 'silhouette_outline',
      description:
        'SEE a body\'s occlusion-correct silhouette from a named view (iso|front|back|left|right|top|bottom, or custom {dir:[x,y,z],xdir:[x,y,z]}) as 2D visible+hidden edge segments + circle centres + bbox — the headless equivalent of looking at the model. Use to confirm overall shape/proportions. Default = all visible bodies.',
      parameters: obj(
        {
          view: { description: 'iso|front|back|left|right|top|bottom, or {dir:[x,y,z],xdir:[x,y,z]}' },
          bodyIds: { type: 'array', items: { type: 'string' }, description: 'optional; omit = all visible' },
        },
        ['view'],
      ),
      execute: async (a, api) => api.silhouetteOutline(a as never),
    },
    {
      name: 'measure_feature',
      description:
        'MEASURE the feature nearest a world point — read back what you actually built: a hole\'s true diameter + depth (axialLength), an achieved fillet radius, an edge length, a planar face\'s area+normal. want: "face" (surface: kind/area/centroid/normal or cylinder radius+diameter+axialLength), "edge" (curve: kind/length/radius+diameter/endpoints), or "auto" (nearest of each). Pair with face_info/edge_info to get a point on the feature.',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          near: { type: 'array', items: { type: 'number' }, description: '[x,y,z] point on/near the feature' },
          want: str('what to measure', ['auto', 'face', 'edge']),
        },
        ['bodyId', 'near'],
      ),
      execute: async (a, api) => api.measureFeature(a.bodyId as string, a.near as [number, number, number], (a.want as never) ?? 'auto'),
    },
    {
      name: 'distance',
      description:
        'EXACT minimum distance (mm) between two bodies, with the closest point on each. Use it to verify clearances and placements ("is the lid 2mm above the box?"). 0 = touching or overlapping — run interference to distinguish.',
      parameters: obj(
        { bodyIdA: str('first body feature id'), bodyIdB: str('second body feature id') },
        ['bodyIdA', 'bodyIdB'],
      ),
      execute: async (a, api) => api.distance(a.bodyIdA as string, a.bodyIdB as string),
    },
    {
      name: 'interference',
      description:
        'INTERFERENCE CHECK: exact overlap volume (mm³) between two bodies. interferes:false means the parts are clear (touching faces count as clear). Use before declaring an assembly valid.',
      parameters: obj(
        { bodyIdA: str('first body feature id'), bodyIdB: str('second body feature id') },
        ['bodyIdA', 'bodyIdB'],
      ),
      execute: async (a, api) => api.interference(a.bodyIdA as string, a.bodyIdB as string),
    },
    {
      name: 'assembly_clash',
      description:
        'WHOLE-ASSEMBLY validation in one call: checks every visible exact-B-rep body pair — conservative bbox prefilter, then exact interference (overlap mm³) on overlapping pairs and exact clearance on pairs within `clearance` mm (default 0.5, clamped ≥0). Returns {bodies, skippedNoBrep (mesh-only bodies NOT checked), interferences:[{a,b (feature ids),aName,bName,overlapVolume}], touching:[{a,b,…}] (contact, distance 0 — CLEAR: mated/stacked parts land here), tooClose:[{a,b,…,distance}] (a real sub-clearance gap)}. Empty interferences+tooClose = clean. Use before declaring a multi-part design done, instead of N² interference calls.',
      parameters: obj(
        { clearance: num('flag pairs closer than this, mm (default 0.5)') },
        [],
      ),
      execute: async (a, api) => api.assemblyClash(a as never),
    },
    {
      name: 'topology_summary',
      description:
        'TOPOLOGY census of a body from the exact B-rep: unique solid/shell/wire/vertex counts plus face and edge histograms BY KIND (plane/cylinder/torus/bspline…, line/circle/bspline…). Sanity-check what an operation really produced: an open-top shelled box = 1 solid, 11 planes (5 outer + 5 inner + rim); a fillet adds cylinders+toruses; solids:2 means a boolean left two disconnected lumps; solids:0 means an open (non-watertight) shell.',
      parameters: obj({ bodyId: str('body feature id') }, ['bodyId']),
      execute: async (a, api) => api.topologySummary(a.bodyId as string),
    },
    {
      name: 'wall_thickness',
      description:
        'DFM probe: sample the body\'s exact skin (default 200 rays, area-weighted) and measure local wall thickness → {min,max,mean,samples,thinnest:[{point,thickness}×5]}. Use after shell/cut operations to catch walls below the printable/moldable minimum (FDM ≳1.2mm, SLA ≳0.6mm, injection 1–3mm); the thinnest[].point coordinates tell you WHERE to thicken. A solid part reports its smallest cross-body span.',
      parameters: obj(
        { bodyId: str('body feature id'), samples: num('sample count 20–2000, default 200') },
        ['bodyId'],
      ),
      execute: async (a, api) => api.wallThickness(a.bodyId as string, (a.samples as number) ?? 200),
    },
    {
      name: 'mate',
      description:
        'ASSEMBLY mate; consumes the moved body. kinds: "anchor" (default) translate so the moving body\'s bbox anchor lands on the other\'s anchor + offset (center|bottom|top|min|max; default stacks bottom-onto-top). "concentric" rotate+translate so a cylindrical face on each becomes COAXIAL (stud into hole). FACE-TO-FACE (pass moveFacePoint/ontoFacePoint near the PLANAR faces, from face_info): "flush" the two faces made coincident and opposing (touching); "distance" flush but held `gap` mm apart; "parallel" normals aligned same direction (no translation); "angle" normals held `angleDeg` apart. Face mates leave the in-plane slide free (add an anchor mate or a second face mate to pin it).',
      parameters: obj(
        {
          moveBodyId: str('body that moves'),
          ontoBodyId: str('reference body (stays)'),
          kind: str('mate kind', ['anchor', 'concentric', 'flush', 'parallel', 'distance', 'angle']),
          moveAnchor: str('anchor on the moving body', ['center', 'bottom', 'top', 'min', 'max']),
          ontoAnchor: str('anchor on the reference body', ['center', 'bottom', 'top', 'min', 'max']),
          offset: { type: 'array', items: { type: 'number' }, description: '[x,y,z] mm extra offset (anchor)' },
          moveFacePoint: { type: 'array', items: { type: 'number' }, description: 'point near the moving body\'s cylindrical/planar face' },
          ontoFacePoint: { type: 'array', items: { type: 'number' }, description: 'point near the reference face' },
          gap: { description: 'distance mate: mm held between the two faces' },
          angleDeg: { description: 'angle mate: degrees between the two face normals' },
        },
        ['moveBodyId', 'ontoBodyId'],
      ),
      execute: async (a, api) => api.mate(a as never),
    },
    {
      name: 'update_feature',
      description:
        'Merge a partial params patch into any feature (e.g. {distance: 30} on an extrude, {entities:[...]} on a sketch). Numeric values may be expressions: {distance:{"$expr":"width/2"}}. Sketches accept {constraints:[...]}: {kind:"horizontal"|"vertical",entity:i} | {kind:"parallel"|"perpendicular"|"equal",a:i,b:i} | {kind:"distance",a:{entity,point},b:{entity,point},value} | {kind:"radius",entity:i,value} | {kind:"coincident",a:{entity,point},b:{entity,point}} | {kind:"fixed",point:{entity,point},at:[x,y]} | {kind:"tangent",a:lineIdx,b:circleIdx} | {kind:"concentric",a:i,b:i} (circles/arcs/ellipses share a center) | {kind:"symmetric",a:{entity,point},b:{entity,point},about:lineIdx} — points: "a"/"b"/"mid" (lines), "center" (circles/arcs), "min"/"max" (rects); values may be {"$expr":"..."}. Constraints are solved at regeneration. See list_features for current params.',
      parameters: obj(
        { id: str('feature id'), params: { type: 'object', description: 'partial params' } },
        ['id', 'params'],
      ),
      execute: async (a, api) => api.updateFeature(a.id as string, a.params as never),
    },
    {
      name: 'delete_feature',
      description: 'Delete a feature and everything downstream of it (cascade).',
      parameters: obj({ id: str('feature id') }, ['id']),
      execute: async (a, api) => api.deleteFeature(a.id as string),
    },
    {
      name: 'rename_feature',
      parameters: obj({ id: str('feature id'), name: str('new name') }, ['id', 'name']),
      description: 'Rename a feature.',
      execute: async (a, api) => api.renameFeature(a.id as string, a.name as string),
    },
    {
      name: 'suppress_feature',
      description: 'Suppress (hide from evaluation) or unsuppress a feature.',
      parameters: obj(
        { id: str('feature id'), suppressed: { type: 'boolean' } },
        ['id', 'suppressed'],
      ),
      execute: async (a, api) => api.suppressFeature(a.id as string, a.suppressed as boolean),
    },
    {
      name: 'undo',
      description: 'Undo the last timeline operation.',
      parameters: obj({}),
      execute: async (_a, api) => api.undo(),
    },
    {
      name: 'redo',
      description: 'Redo the last undone timeline operation.',
      parameters: obj({}),
      execute: async (_a, api) => api.redo(),
    },
    {
      name: 'checkpoint',
      description:
        'Grab a checkpoint token BEFORE a risky multi-step build. Later rollback(token) restores exactly this state — safer than counting undos when a middle step fails. The token is bound to the current project and to states within the last 200 operations.',
      parameters: obj({}),
      execute: async (_a, api) => api.checkpoint(),
    },
    {
      name: 'rollback',
      description:
        'Undo every operation performed since a checkpoint() token — clean bailout from a build that went wrong. Returns how many steps were undone. Errors if you have since switched projects or the checkpoint is older than the 200-op history limit.',
      parameters: obj({ checkpoint: str('the token string returned by checkpoint()') }, ['checkpoint']),
      execute: async (a, api) => api.rollback(a.checkpoint as string),
    },
    {
      name: 'dry_run',
      description:
        'TRIAL a timeline tool without keeping it: executes `tool` with `args`, reports {ok, result|error, sceneDiff:{newBodies, removedBodies, changedBodies, newErrors}}, then ALWAYS rolls the document back — the model is untouched either way (feature ids in the result no longer exist afterwards). Use before a risky operation (big fillet, boolean on complex bodies, shell) to learn whether it succeeds and what it would change, without spending an undo step or breaking the user\'s model. Timeline-mutating tools only (extrude, fillet, press_pull, update_feature…).',
      parameters: obj(
        {
          tool: str('name of the built-in timeline tool to trial'),
          args: { type: 'object', description: 'the arguments that tool would receive' },
        },
        ['tool'],
      ),
      execute: async (a, api) => {
        const name = a.tool as string;
        if (!DRY_RUNNABLE.has(name)) {
          throw new Error(
            `dry_run only trials timeline-mutating tools (got "${name}"). Allowed: ${[...DRY_RUNNABLE].join(', ')}`,
          );
        }
        const target = builtinTools().find((t) => t.name === name);
        if (!target) throw new Error(`unknown tool "${name}"`);
        const cp = api.checkpoint();
        const before = api.sceneInfo();
        const preIds = new Set(api.listFeatures().map((f: { id: string }) => f.id));
        let outcome: Record<string, unknown>;
        let createdId: string | null = null;
        try {
          const result = await target.execute((a.args as Record<string, unknown>) ?? {}, api);
          // Only ids the trial CREATED need deselecting after rollback —
          // tools like set_material return the id of a still-live feature.
          const rid =
            result && typeof result === 'object' && 'id' in result ? String(result.id) : null;
          createdId = rid && !preIds.has(rid) ? rid : null;
          const after = api.sceneInfo();
          const beforeById = new Map(before.bodies.map((b) => [b.id, b]));
          const afterIds = new Set(after.bodies.map((b) => b.id));
          const rv = (v: number): number => Math.round(v * 1000) / 1000;
          const bboxMoved = (p: (typeof before.bodies)[0], b: (typeof before.bodies)[0]): boolean =>
            ([0, 1, 2] as const).some(
              (i) => Math.abs(p.bbox.min[i] - b.bbox.min[i]) > 1e-6 || Math.abs(p.bbox.max[i] - b.bbox.max[i]) > 1e-6,
            );
          outcome = {
            ok: true,
            result,
            sceneDiff: {
              newBodies: after.bodies
                .filter((b) => !beforeById.has(b.id))
                .map((b) => ({ id: b.id, name: b.name, volume: rv(b.volume), bbox: b.bbox })),
              removedBodies: before.bodies
                .filter((b) => !afterIds.has(b.id))
                .map((b) => ({ id: b.id, name: b.name })),
              changedBodies: after.bodies
                .filter((b) => {
                  const prev = beforeById.get(b.id);
                  return prev && (Math.abs(prev.volume - b.volume) > 1e-6 || bboxMoved(prev, b));
                })
                .map((b) => ({
                  id: b.id,
                  name: b.name,
                  volumeBefore: rv(beforeById.get(b.id)!.volume),
                  volumeAfter: rv(b.volume),
                  bbox: b.bbox,
                })),
              newErrors: after.errors.filter((e) => !before.errors.some((p) => p.id === e.id)),
            },
          };
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          outcome = { ok: false, error: message, ...(classifyToolError(message) ?? {}) };
        }
        // Roll back OUTSIDE the try so a rollback failure (project switched
        // mid-trial) augments the outcome instead of masking it as a throw.
        try {
          await api.rollback(cp.checkpoint);
          // The trial must not linger: not redo-reachable, not selected.
          api.discardRedo();
          if (createdId) api.selectFeature(null);
        } catch (err) {
          outcome.rollbackFailed = err instanceof Error ? err.message : String(err);
        }
        return outcome;
      },
    },
    {
      name: 'cross_section',
      description:
        'SEE the exact cut outline of a body at a plane (axis=X|Y|Z, offset mm) as 2D segments + bbox + closedLoops (count of closed contours — e.g. a tube section = 2, a solid = 1) — without a screenshot. Use to confirm internal shape (a bore diameter, a rib, wall thickness) is what you intended. 2D coords: X→(Y,Z), Y→(X,Z), Z→(X,Y).',
      parameters: obj(
        {
          bodyId: str('body feature id'),
          axis: str('section plane normal axis', ['X', 'Y', 'Z']),
          offset: num('plane position along the axis, mm'),
        },
        ['bodyId', 'axis', 'offset'],
      ),
      execute: async (a, api) => api.crossSection(a.bodyId as string, a.axis as 'X' | 'Y' | 'Z', a.offset as number),
    },
    {
      name: 'select_feature',
      description:
        'Highlight a feature in the GUI (null clears). Use it to SHOW the human which body/sketch you mean while discussing the design.',
      parameters: obj({ id: str('feature id, or null to clear') }, []),
      execute: async (a, api) => api.selectFeature((a.id as string) ?? null),
    },
    {
      name: 'export_step',
      description:
        'EXPORT exact B-rep solids as STEP text (AP203, XCAF product names from feature names). Default = all visible bodies; bodyIds narrows it. The text is LARGE — save it to a file (MCP clients) or hand it to the user; do not read it.',
      parameters: obj(
        { bodyIds: { type: 'array', items: { type: 'string' }, description: 'optional feature ids; omit = all visible' } },
        [],
      ),
      execute: async (a, api) => api.exportStep(a as never),
    },
    {
      name: 'fit_view',
      description: 'Fit the camera to the whole model.',
      parameters: obj({}),
      execute: async (_a, api) => api.fitView(),
    },
    {
      name: 'view_model',
      description:
        'LOOK at the model: capture the 3D viewport and receive it as image(s) (requires a vision-capable model). Use to verify your work visually, judge proportions, or understand geometry hard to infer from params. Pass views=["iso","top"] for multiple angles in one call — the user camera is not disturbed. Omit views to see exactly what the user currently sees.',
      parameters: obj({
        views: {
          type: 'array',
          items: {
            type: 'string',
            enum: ['iso', 'front', 'back', 'left', 'right', 'top', 'bottom'],
          },
          description: 'standard directions to capture; omit = current user view',
        },
        fit: { type: 'boolean', description: 'fit view first (current-view mode only), default true' },
      }),
      execute: async () => {
        throw new Error('handled by assistant'); // intercepted in the agent loop
      },
    },
    {
      name: 'run_js',
      description:
        'Run JavaScript with `api` (the CadApi: every tool above as a method — createSketch, extrude, moveBody, listFeatures, bodyInfo, updateFeature…) and return a JSON-able value. Use for math, loops, and multi-step construction. Example: `const s = await api.createSketch({entities:[{kind:"circle",center:[0,0],radius:10}]}); return api.extrude({sketchId:s.id,distance:20});`',
      parameters: obj({ code: str('JS body; may use await; return a value') }, ['code']),
      execute: async (a, api) => runCode(a.code as string, api, {}),
    },
    {
      name: 'define_tool',
      description:
        'Create a PERSISTENT named tool from JS code. It immediately joins your toolset for future calls and can be published to the GUI. code receives (api, params). parameters must be a JSON schema object describing params.',
      parameters: obj(
        {
          name: str('snake_case tool name'),
          description: str('what it does, for humans and future prompts'),
          parameters: { type: 'object', description: 'JSON schema for params' },
          code: str('JS body: (api, params) available; may use await; return a value'),
        },
        ['name', 'description', 'parameters', 'code'],
      ),
      execute: async () => {
        throw new Error('handled by registry'); // replaced in AssistantToolbox
      },
    },
    {
      name: 'publish_tool',
      description:
        'Publish a previously defined custom tool to the GUI toolbar so the user can run it with a parameter form. Set published=false to unpublish.',
      parameters: obj(
        { name: str('custom tool name'), published: { type: 'boolean', description: 'default true' } },
        ['name'],
      ),
      execute: async () => {
        throw new Error('handled by registry');
      },
    },
  ];
}

/** Execute custom/one-off JS with the api in scope. */
export async function runCode(
  code: string,
  api: CadApi,
  params: Record<string, unknown>,
): Promise<unknown> {
  const fn = new Function(
    'api',
    'params',
    `"use strict"; return (async () => { ${code}\n })();`,
  ) as (api: CadApi, params: Record<string, unknown>) => Promise<unknown>;
  return fn(api, params);
}

/** Custom tools persisted in IndexedDB + change notification for UIs. */
export class AssistantToolbox {
  customTools: CustomTool[] = [];
  onChange: (() => void) | null = null;

  constructor(readonly api: CadApi) {}

  async load(): Promise<void> {
    this.customTools = await getSetting<CustomTool[]>('customTools', []);
    this.onChange?.();
  }

  private async persist(): Promise<void> {
    await setSetting('customTools', this.customTools);
    this.onChange?.();
  }

  async define(tool: Omit<CustomTool, 'published'>): Promise<void> {
    if (!/^[a-z][a-z0-9_]*$/i.test(tool.name)) {
      throw new Error('Tool name must be an identifier (letters, digits, _)');
    }
    if (builtinTools().some((b) => b.name === tool.name)) {
      throw new Error(`"${tool.name}" collides with a built-in tool`);
    }
    const existing = this.customTools.find((t) => t.name === tool.name);
    if (existing) Object.assign(existing, tool);
    else this.customTools.push({ ...tool, published: false });
    await this.persist();
  }

  async setPublished(name: string, published: boolean): Promise<void> {
    const tool = this.customTools.find((t) => t.name === name);
    if (!tool) throw new Error(`No custom tool "${name}"`);
    tool.published = published;
    await this.persist();
  }

  async remove(name: string): Promise<void> {
    this.customTools = this.customTools.filter((t) => t.name !== name);
    await this.persist();
  }

  async run(name: string, args: Record<string, unknown>): Promise<unknown> {
    const tool = this.customTools.find((t) => t.name === name);
    if (!tool) throw new Error(`No custom tool "${name}"`);
    return runCode(tool.code, this.api, args);
  }

  /** Full toolset for the LLM: built-ins + custom, with meta-tools wired. */
  allTools(): ToolDef[] {
    const tools = builtinTools().map((t) => {
      if (t.name === 'define_tool') {
        return {
          ...t,
          execute: async (a: Record<string, unknown>): Promise<unknown> => {
            await this.define({
              name: a.name as string,
              description: a.description as string,
              parameters: (a.parameters as ToolSchema) ?? { type: 'object', properties: {} },
              code: a.code as string,
            });
            return { defined: a.name, note: 'callable now; publish_tool adds it to the GUI' };
          },
        };
      }
      if (t.name === 'publish_tool') {
        return {
          ...t,
          execute: async (a: Record<string, unknown>): Promise<unknown> => {
            await this.setPublished(a.name as string, (a.published as boolean) ?? true);
            return { name: a.name, published: (a.published as boolean) ?? true };
          },
        };
      }
      return t;
    });
    for (const c of this.customTools) {
      tools.push({
        name: c.name,
        description: `[custom] ${c.description}`,
        parameters: c.parameters,
        execute: (args, api) => runCode(c.code, api, args),
      });
    }
    return tools;
  }
}
